import { useEffect, useState, useCallback, useRef } from 'react'
import { initializePushNotifications, subscribeToPush, unsubscribeFromPush, isSubscribed, isPushSupported, requestNotificationPermission } from '../utils/pushNotifications'
import { isUserAuthenticated } from '../utils/authManager'
import { Alert } from 'components'

/**
 * Hook to manage push notifications
 * Automatically initializes push notifications when user is authenticated
 */
export const usePushNotifications = () => {
  const [pushState, setPushState] = useState({
    supported: false,
    subscribed: false,
    loading: true,
    error: null
  })
  const isUnsubscribing = useRef(false)

  const checkSubscription = useCallback(async () => {
    if (!isPushSupported()) {
      setPushState({
        supported: false,
        subscribed: false,
        loading: false,
        error: null
      })
      return
    }

    try {
      const subscribed = await isSubscribed()
      setPushState(prev => ({
        ...prev,
        subscribed,
        loading: false
      }))
    } catch (error) {
      setPushState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }))
    }
  }, [])

  const subscribe = useCallback(async () => {
    if (!isUserAuthenticated()) {
      setPushState(prev => ({
        ...prev,
        error: 'User must be authenticated to subscribe to push notifications'
      }))
      return { success: false, error: 'User not authenticated' }
    }

    setPushState(prev => ({ ...prev, loading: true, error: null }))

    try {
      await subscribeToPush()
      await checkSubscription()
      return { success: true }
    } catch (error) {
      setPushState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }))
      return { success: false, error: error.message }
    }
  }, [checkSubscription])

  const unsubscribe = useCallback(async () => {
    if (isUnsubscribing.current) {
      return { success: false, error: 'Unsubscribe already in progress' }
    }
    
    isUnsubscribing.current = true
    setPushState(prev => ({ ...prev, loading: true, error: null }))

    try {
      await unsubscribeFromPush()
      
      // Immediately set state to unsubscribed - don't wait for check
      setPushState(prev => ({
        ...prev,
        subscribed: false,
        loading: false
      }))
      
      isUnsubscribing.current = false
      return { success: true }
    } catch (error) {
      setPushState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }))
      isUnsubscribing.current = false
      return { success: false, error: error.message }
    }
  }, [])

  useEffect(() => {
    // Check authentication status periodically until authenticated
    const checkAuthAndInit = async () => {
      const isAuthenticated = isUserAuthenticated()
      
      if (!isAuthenticated) {
        setPushState({
          supported: false,
          subscribed: false,
          loading: false,
          error: null
        })
        return false
      }

      // Initialize push notifications
      setPushState(prev => ({ ...prev, loading: true }))
      
      try {
        // Don't re-initialize if we're in the middle of unsubscribing
        if (isUnsubscribing.current) {
          return true
        }
        
        const result = await initializePushNotifications()
        
        // Log result for debugging (especially useful in Android WebView)
        if (typeof window !== 'undefined' && window.console) {
          console.log('[PushNotifications] Initialization result:', {
            supported: result.supported,
            subscribed: result.subscribed,
            error: result.error,
            permission: result.permission
          })
        }
        
        // Only update subscribed state if we're not unsubscribing
        if (!isUnsubscribing.current) {
          setPushState({
            supported: result.supported,
            subscribed: result.subscribed || false,
            loading: false,
            error: result.error || null
          })
        }
        return true
      } catch (error) {
        // Log error for debugging
        if (typeof window !== 'undefined' && window.console) {
          console.error('[PushNotifications] Initialization error:', error)
        }
        
        if (!isUnsubscribing.current) {
          setPushState({
            supported: false,
            subscribed: false,
            loading: false,
            error: error.message
          })
        }
        return true // Stop retrying on error
      }
    }

    // Try immediately
    checkAuthAndInit().then(initialized => {
      // If not initialized, retry every 2 seconds for up to 10 seconds
      if (!initialized) {
        let attempts = 0
        const maxAttempts = 5
        const retryInterval = setInterval(() => {
          attempts++
          checkAuthAndInit().then(success => {
            if (success || attempts >= maxAttempts) {
              clearInterval(retryInterval)
            }
          })
        }, 2000)
        
        // Cleanup after max attempts
        setTimeout(() => clearInterval(retryInterval), maxAttempts * 2000)
      }
    })
  }, [])

  return {
    ...pushState,
    subscribe,
    unsubscribe,
    refresh: checkSubscription,
    requestPermission: async () => {
      console.log('[usePushNotifications] === REQUEST PERMISSION CALLED ===')
      const isWebView = navigator.userAgent.includes('wv') || navigator.userAgent.includes('BCNIndiaApp')
      console.log('[usePushNotifications] Is WebView:', isWebView)
      console.log('[usePushNotifications] User Agent:', navigator.userAgent)
      
      // For Android WebView, skip JavaScript permission check and go straight to FCM subscription
      if (isWebView) {
        console.log('[usePushNotifications] Android WebView detected, checking authentication...')
        const authenticated = isUserAuthenticated()
        console.log('[usePushNotifications] User authenticated:', authenticated)
        
        if (!authenticated) {
          console.error('[usePushNotifications] User not authenticated')
          Alert.error('Please login to enable notifications')
          return { success: false, error: 'User not authenticated' }
        }
        
        // Check if Android FCM interface is available
        console.log('[usePushNotifications] Checking Android FCM interface...')
        console.log('[usePushNotifications] window.Android:', typeof window !== 'undefined' ? (window.Android ? 'exists' : 'missing') : 'window undefined')
        console.log('[usePushNotifications] window.Android.getFCMToken:', typeof window !== 'undefined' && window.Android ? (window.Android.getFCMToken ? 'exists' : 'missing') : 'N/A')
        
        if (typeof window === 'undefined' || !window.Android || !window.Android.getFCMToken) {
          console.error('[usePushNotifications] FCM interface not available')
          Alert.error('FCM interface not available. Please ensure you are using the latest version of the Android app.')
          return { success: false, error: 'FCM interface not available' }
        }
        
        console.log('[usePushNotifications] ✅ FCM interface available, calling subscribe()...')
        // For WebView, Android permission is already granted, so skip JavaScript permission request
        // and go straight to FCM subscription
        try {
          const result = await subscribe()
          console.log('[usePushNotifications] ✅ Subscribe result:', result)
          return result
        } catch (error) {
          console.error('[usePushNotifications] ❌ Subscribe failed:', error)
          Alert.error('Failed to enable notifications: ' + error.message)
          return { success: false, error: error.message }
        }
      }
      
      // For regular browsers, check support and request permission normally
      if (!isPushSupported()) {
        Alert.error('Push notifications are not supported in this browser')
        return { success: false, error: 'Not supported' }
      }
      
      if (!isUserAuthenticated()) {
        Alert.error('Please login to enable notifications')
        return { success: false, error: 'User not authenticated' }
      }
      
      try {
        const granted = await requestNotificationPermission()
        
        if (granted) {
          const result = await subscribe()
          return result
        } else {
          Alert.error('Notification permission was denied. Please enable it in your browser settings.')
          return { success: false, error: 'Permission denied' }
        }
      } catch (error) {
        Alert.error('Failed to enable notifications: ' + error.message)
        return { success: false, error: error.message }
      }
    }
  }
}
